import React, { useState, useEffect, useRef } from 'react';
import API from '../../services/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { COMPANY_INFO } from '../../constants/companyInfo';
import { Save, Plus, Trash2, Download, Wallet, ArrowRightLeft, Hash, Phone, AlertCircle, Printer, Send } from 'lucide-react';

// ==========================================
// SEARCHABLE SELECT COMPONENT
// ==========================================
const SearchableSelect = ({ value, onChange, placeholder, subSubHeadFilter, restrictToId, isTable = false, filterCash = false }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [options, setOptions] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const [dropdownStyles, setDropdownStyles] = useState({});
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);

    const getAccountData = async (queryStr) => {
        try {
            const res = await API.get(`/vouchers/search?${queryStr}`);
            return res.data?.data || res.data || [];
        } catch (err) {
            console.error("Search API Call Error: ", err);
            return [];
        }
    };

    useEffect(() => {
        if (value && typeof value !== 'object') {
            getAccountData(`search=${value}`).then(data => {
                const found = data.find(a => a.id == value);
                if (found) {
                    setSearchTerm(found.title);
                }
            });
        } else if (!value) { 
            setSearchTerm(''); 
        }
    }, [value]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (restrictToId) {
                    const data = await getAccountData(`search=${restrictToId}`);
                    const found = data.filter(a => a.id == restrictToId) || [];
                    setOptions(found);
                    return;
                }

                let queryStr = `search=${encodeURIComponent(searchTerm)}&limit=200`;
                
                if (subSubHeadFilter) {
                    queryStr += `&SubSubHeadID=${encodeURIComponent(subSubHeadFilter)}`;
                }

                let rawData = await getAccountData(queryStr);
                if (!Array.isArray(rawData)) rawData = [];

                let filteredData = rawData;
                if (subSubHeadFilter) {
                    filteredData = filteredData.filter(a => {
                        const accSubHead = String(a.SubSubHeadID || a.sub_sub_head_id || '').trim();
                        const filterVal = String(subSubHeadFilter).trim();
                        return accSubHead === filterVal;
                    });
                }
                
                if (filterCash) {
                    filteredData = filteredData.filter(a => {
                        const titleLower = (a.title || '').toLowerCase();
                        return !titleLower.includes('cash');
                    });
                }

                setOptions(filteredData);
            } catch (err) {
                console.error("Suggestions fetch failed: ", err);
            }
        };

        if (isOpen) {
            const delayDebounce = setTimeout(fetchData, 150);
            return () => clearTimeout(delayDebounce);
        }
    }, [searchTerm, isOpen, subSubHeadFilter, restrictToId, filterCash]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Calculate fixed position so dropdown never gets clipped inside the table box
    const handleFocus = () => {
        if (inputRef.current) {
            const rect = inputRef.current.getBoundingClientRect();
            setDropdownStyles({
                position: 'fixed',
                top: `${rect.bottom + 4}px`,
                left: `${rect.left}px`,
                width: `${rect.width}px`,
                zIndex: 99999,
                background: '#fff',
                border: '1px solid #cbd5e1',
                borderRadius: '10px',
                boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
                maxHeight: '220px',
                overflowY: 'auto'
            });
        }
        setIsOpen(true);
    };

    const handleKeyDown = (e) => {
        if (!isOpen || options.length === 0) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightedIndex(prev => (prev < options.length - 1 ? prev + 1 : 0));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightedIndex(prev => (prev > 0 ? prev - 1 : options.length - 1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const selectedOpt = options[highlightedIndex];
            if (selectedOpt) {
                onChange(selectedOpt);
                setSearchTerm(selectedOpt.title);
                setIsOpen(false);
            }
        }
    };

    return (
        <div ref={wrapperRef} style={{ position: 'relative', width: '100%' }}>
            <input
                ref={inputRef}
                type="text"
                style={isTable ? styles.inputTable : styles.inputMain}
                placeholder={placeholder}
                value={searchTerm}
                onChange={(e) => { 
                    setSearchTerm(e.target.value); 
                    setIsOpen(true); 
                    setHighlightedIndex(0);
                    handleFocus();
                }}
                onFocus={handleFocus}
                onKeyDown={handleKeyDown}
            />
            {isOpen && options.length > 0 && (
                <div style={dropdownStyles}>
                    {options.map((opt, idx) => (
                        <div 
                            key={opt.id || idx} 
                            style={{ 
                                ...styles.dropdownItem, 
                                backgroundColor: idx === highlightedIndex ? '#f1f5f9' : '#fff' 
                            }} 
                            onMouseEnter={() => setHighlightedIndex(idx)}
                            onMouseDown={() => { 
                                onChange(opt); 
                                setSearchTerm(opt.title); 
                                setIsOpen(false); 
                            }}
                        >
                            <div style={{ fontWeight: '700', color: '#1c313a', fontSize: '13px' }}>{opt.title}</div>
                            <div style={{ fontSize: '10px', color: '#78909c' }}>
                                ID: {opt.id} | SubSubHeadID: {opt.SubSubHeadID || opt.sub_sub_head_id || 'N/A'} {opt.phone ? `| Ph: ${opt.phone}` : ''}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// ==========================================
// MAIN VOUCHER SYSTEM COMPONENT
// ==========================================
const VoucherSystem = () => {
    const [loading, setLoading] = useState(false);
    
    const [systemIds] = useState({ 
        cash_sub_sub_head_id: '521000', 
        bank_sub_sub_head_id: '522000'
    });

    const [selectedControlAcc, setSelectedControlAcc] = useState(null);
    const [header, setHeader] = useState({
        voucher_type: 'CPV',
        transaction_date: new Date().toISOString().split('T')[0],
        cash_account_id: '',
        sub_sub_head_filter: '521000',
        narration: ''
    });

    const [entries, setEntries] = useState([{ 
        account_id: '', 
        account_detail: null, 
        dr_account_id: '', 
        dr_account_detail: null, 
        cr_account_id: '', 
        cr_account_detail: null, 
        invoice_no: '', 
        remarks: '', 
        amount: '' 
    }]);

    const fetchFirstAccountBySubHead = async (subSubHeadId) => {
        try {
            const res = await API.get(`/vouchers/search?SubSubHeadID=${subSubHeadId}&limit=1`);
            const dataList = res.data?.data || res.data || [];
            return dataList.length > 0 ? dataList[0] : null;
        } catch (err) {
            console.error("Control Account Fetch Error: ", err);
            return null;
        }
    };

    useEffect(() => {
        const loadInitialControl = async () => {
            if (header.voucher_type === 'CPV' || header.voucher_type === 'CRV') {
                const targetSubHead = systemIds.cash_sub_sub_head_id;
                setHeader(prev => ({ ...prev, cash_account_id: '', sub_sub_head_filter: targetSubHead }));
                
                try {
                    const cashAcc = await fetchFirstAccountBySubHead(targetSubHead);
                    if (cashAcc) {
                        setHeader(prev => ({ ...prev, cash_account_id: cashAcc.id, sub_sub_head_filter: targetSubHead }));
                        setSelectedControlAcc(cashAcc);
                    } else {
                        setSelectedControlAcc(null);
                    }
                } catch (err) {
                    console.error("Error setting initial cash account: ", err);
                }
            } else if (header.voucher_type === 'BPV' || header.voucher_type === 'BRV') {
                const targetSubHead = systemIds.bank_sub_sub_head_id;
                setHeader(prev => ({ ...prev, cash_account_id: '', sub_sub_head_filter: targetSubHead }));
                setSelectedControlAcc(null);
            } else {
                setHeader(prev => ({ ...prev, cash_account_id: '', sub_sub_head_filter: '' }));
                setSelectedControlAcc(null);
            }

            if (header.voucher_type === 'JV') {
                setEntries([{ dr_account_id: '', dr_account_detail: null, cr_account_id: '', cr_account_detail: null, invoice_no: '', remarks: '', amount: '' }]);
            } else {
                setEntries([{ account_id: '', account_detail: null, invoice_no: '', remarks: '', amount: '' }]);
            }
        };

        loadInitialControl();
    }, [header.voucher_type, systemIds]);

    const totalAmount = entries.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);

    const updateEntry = (index, field, value, detailValue = null) => {
        const newEntries = [...entries];
        newEntries[index][field] = value;
        if (detailValue !== null) {
            newEntries[index][field + '_detail'] = detailValue;
        }
        setEntries(newEntries);
    };

    const getFlowDirectionText = () => {
        if (header.voucher_type === 'CPV') return "Paying Out: From Cash Control Account ➔ To Multiple Ledger Accounts Below";
        if (header.voucher_type === 'BPV') return "Paying Out: From Bank Control Account ➔ To Multiple Ledger Accounts Below";
        if (header.voucher_type === 'CRV') return "Receiving In: From Multiple Ledger Accounts Below ➔ Into Cash Control Account";
        if (header.voucher_type === 'BRV') return "Receiving In: From Multiple Ledger Accounts Below ➔ Into Bank Control Account";
        return "Journal Adjustment: Direct balance shifting between ledgers (Debit to Credit)";
    };

    const exportPDF = () => {
        const doc = new jsPDF('p', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 15;

        const companyName = COMPANY_INFO?.name || "BIKE INVENTORY SYSTEM";
        const companyTagline = COMPANY_INFO?.tagline || "Deals in All Kinds of Bikes & Spare Parts";
        const companyAddress = COMPANY_INFO?.address || "Main Road, Pakistan";
        const companyPhone = COMPANY_INFO?.phone || "N/A";
        const companyEmail = COMPANY_INFO?.email || "N/A";

        doc.setFont("helvetica", "bold");
        doc.setFontSize(18);
        doc.setTextColor(44, 62, 80); 
        const splitCompanyName = doc.splitTextToSize(companyName, pageWidth - 100);
        doc.text(splitCompanyName, margin, 18);
        
        let nextY = 18 + (splitCompanyName.length * 6);

        doc.setFontSize(8.5);
        doc.setFont("helvetica", "italic");
        doc.setTextColor(127, 140, 141); 
        doc.text(companyTagline, margin, nextY);
        nextY += 5;

        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(52, 73, 94);
        doc.text(companyAddress, margin, nextY);
        nextY += 5;
        doc.text(`Phone: ${companyPhone} | Email: ${companyEmail}`, margin, nextY);

        const metaBoxX = pageWidth - 80;
        const metaBoxY = 14;
        const metaBoxW = 65;
        const metaBoxH = 28;

        doc.setFillColor(245, 247, 250);
        doc.roundedRect(metaBoxX, metaBoxY, metaBoxW, metaBoxH, 2, 2, 'F');
        doc.setDrawColor(220, 224, 230);
        doc.setLineWidth(0.3);
        doc.roundedRect(metaBoxX, metaBoxY, metaBoxW, metaBoxH, 2, 2, 'D');

        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.setTextColor(22, 160, 133); 
        doc.text(`${header.voucher_type} VOUCHER`, metaBoxX + 5, metaBoxY + 6);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(8.5);
        doc.setTextColor(44, 62, 80);
        doc.text(`Date: ${header.transaction_date}`, metaBoxX + 5, metaBoxY + 12);
        
        let controlAccName = selectedControlAcc ? `${selectedControlAcc.title}` : (header.voucher_type === 'JV' ? 'Journal Entry' : 'N/A');
        const splitControl = doc.splitTextToSize(`Control: ${controlAccName}`, metaBoxW - 10);
        doc.text(splitControl, metaBoxX + 5, metaBoxY + 18);

        doc.setDrawColor(22, 160, 133);
        doc.setLineWidth(0.8);
        doc.line(margin, 44, pageWidth - margin, 44);

        const tableBody = entries.map(e => {
            let accountTitleDisplay = '-';
            
            if (header.voucher_type === 'JV') {
                const drName = e.dr_account_detail?.title || 'N/A';
                const crName = e.cr_account_detail?.title || 'N/A';
                accountTitleDisplay = `Dr: ${drName}\nCr: ${crName}`;
            } else {
                accountTitleDisplay = e.account_detail?.title || 'N/A';
            }

            return [
                e.invoice_no || '-',
                accountTitleDisplay,
                e.remarks || header.narration || '-', 
                { content: Number(e.amount || 0).toLocaleString(undefined, {minimumFractionDigits: 2}), styles: { halign: 'right' } }
            ];
        });

        autoTable(doc, {
            startY: 48,
            margin: { left: margin, right: margin },
            head: [['REF / INV', 'ACCOUNT TITLE', 'PARTICULARS / REMARKS', 'AMOUNT (PKR)']],
            body: tableBody,
            theme: 'striped',
            headStyles: { 
                fillColor: [44, 62, 80], 
                textColor: [255, 255, 255], 
                fontSize: 9, 
                fontStyle: 'bold',
                halign: 'left',
                cellPadding: 4
            },
            styles: { 
                fontSize: 8.5, 
                cellPadding: 4.5, 
                valign: 'middle',
                overflow: 'linebreak' 
            },
            columnStyles: {
                0: { cellWidth: 25 },  
                1: { cellWidth: 65 },  
                2: { cellWidth: 60 },  
                3: { cellWidth: 30, halign: 'right' }   
            },
            foot: [[
                { content: 'Grand Total in Words: ' + totalAmount.toLocaleString() + ' PKR Only', colSpan: 2, styles: { fontSize: 7.5, fontStyle: 'italic', textColor: [100, 100, 100] } },
                { content: 'Total', styles: { halign: 'right', fontStyle: 'bold', fontSize: 9 } },
                { content: totalAmount.toLocaleString(undefined, {minimumFractionDigits: 2}), styles: { halign: 'right', fontStyle: 'bold', fontSize: 9, textColor: [22, 160, 133] } }
            ]],
            footStyles: { fillColor: [245, 247, 250], textColor: [44, 62, 80], cellPadding: 4 }
        });

        const finalY = doc.lastAutoTable.finalY + 25;
        doc.setDrawColor(189, 195, 199);
        doc.setLineWidth(0.3);
        
        doc.line(margin, finalY, margin + 45, finalY);
        doc.setFontSize(8.5);
        doc.setFont("helvetica", "normal");
        doc.text("Prepared By", margin, finalY + 5);

        doc.line(pageWidth - margin - 45, finalY, pageWidth - margin, finalY);
        doc.text("Authorized Signature", pageWidth - margin - 45, finalY + 5);

        doc.save(`${header.voucher_type}_${header.transaction_date}.pdf`);
    };

    const handlePostOnly = async () => {
        if (header.voucher_type !== 'JV' && !header.cash_account_id) return alert("Select Control Account!");
        setLoading(true);
        try {
            const res = await API.post('/vouchers/multi', { 
                ...header, 
                entries, 
                description: header.narration || entries[0]?.remarks || `${header.voucher_type} Entry` 
            });
            if (res.data.success) {
                alert("Voucher Posted Successfully!");
            }
        } catch (err) { 
            console.error(err);
            alert("Posting Error details: " + (err.response?.data?.error || err.message)); 
        } finally { setLoading(false); }
    };

    const handlePostAndPrint = async () => {
        if (header.voucher_type !== 'JV' && !header.cash_account_id) return alert("Select Control Account!");
        setLoading(true);
        try {
            const res = await API.post('/vouchers/multi', { 
                ...header, 
                entries, 
                description: header.narration || entries[0]?.remarks || `${header.voucher_type} Entry` 
            });
            if (res.data.success) {
                exportPDF();
                alert("Voucher Posted & Exported Successfully!");
            }
        } catch (err) { 
            console.error(err);
            alert("Posting Error details: " + (err.response?.data?.error || err.message)); 
        } finally { setLoading(false); }
    };

    const voucherMenu = [
        { type: 'CPV', label: 'Cash Payment (CPV)' },
        { type: 'CRV', label: 'Cash Receipt (CRV)' },
        { type: 'BPV', label: 'Bank Payment (BPV)' },
        { type: 'BRV', label: 'Bank Receipt (BRV)' },
        { type: 'JV',  label: 'Journal Voucher (JV)' },
    ];

    return (
        <div style={styles.container}>
            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
            {loading && (
                <div style={loaderStyles.overlay}>
                    <div style={loaderStyles.container}>
                        <div style={loaderStyles.spinner}></div>
                        <p style={loaderStyles.text}>Processing Voucher...</p>
                        <p style={loaderStyles.subText}>Please wait while we process your transaction</p>
                    </div>
                </div>
            )}
            <div style={styles.mainWrapper}>
                
                {/* --- TOP HEADER TITLE BAR --- */}
                <div style={styles.topSection}>
                    <div style={styles.brandGroup}>
                        <div style={styles.iconBox}><Wallet size={22} color="#fff" /></div>
                        <div>
                            <h2 style={styles.pageTitle}>Voucher Management System</h2>
                            <span style={{ fontSize: '11px', color: '#b0bec5' }}>Create, Post and Print Multi-line Financial Vouchers</span>
                        </div>
                    </div>
                    
                    <div style={styles.actionGroup}>
                        <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#b0bec5' }}>Date:</label>
                        <input 
                            type="date" 
                            style={styles.dateTop} 
                            value={header.transaction_date} 
                            onChange={e => setHeader({...header, transaction_date: e.target.value})} 
                        />
                    </div>
                </div>

                {/* --- KHULA-KHULA MENU BAR --- */}
                <div style={styles.voucherNavBar}>
                    {voucherMenu.map((item) => {
                        const isActive = header.voucher_type === item.type;
                        return (
                            <button 
                                key={item.type}
                                type="button"
                                style={isActive ? styles.navTabActive : styles.navTab}
                                onClick={() => setHeader({ ...header, voucher_type: item.type })}
                            >
                                <span style={styles.navBadge}>{item.type}</span>
                                <span style={styles.navLabel}>{item.label}</span>
                            </button>
                        );
                    })}
                </div>

                {/* --- WORKFLOW EXPLANATOR --- */}
                <div style={styles.flowBar}>
                    <span style={{ marginRight: '6px' }}>🔄</span> {getFlowDirectionText()}
                </div>

                {/* --- CONTROL ACCOUNT SELECTOR --- */}
                <div style={styles.controlBox}>
                    {header.voucher_type !== 'JV' && (
                        <div>
                            <label style={styles.sectionLabel}>
                                {header.voucher_type.startsWith('C') ? 'Cash Control Account (521000)' : 'Bank Control Account (522000)'}
                            </label>
                            <SearchableSelect 
                                value={header.cash_account_id} 
                                subSubHeadFilter={header.sub_sub_head_filter}
                                filterCash={false} 
                                placeholder={header.voucher_type.startsWith('C') ? "Search Cash Accounts..." : "Search Bank Accounts..."} 
                                onChange={acc => { setHeader({...header, cash_account_id: acc.id}); setSelectedControlAcc(acc); }} 
                            />
                        </div>
                    )}
                    <div>
                        <label style={styles.sectionLabel}>General Voucher Narration / Description</label>
                        <input 
                            type="text" 
                            style={styles.inputMain} 
                            placeholder="Optional general narration for voucher..." 
                            value={header.narration}
                            onChange={e => setHeader({...header, narration: e.target.value})}
                        />
                    </div>
                    {header.voucher_type !== 'JV' && (
                        <div style={selectedControlAcc ? styles.detailCardActive : styles.detailCardEmpty}>
                            {selectedControlAcc ? (
                                <div style={styles.detailGrid}>
                                    <div style={{gridColumn: 'span 2', borderBottom: '1px solid #eee', paddingBottom: '4px'}}>
                                        <b style={{fontSize: '13px', color: '#1c313a'}}>{selectedControlAcc.title}</b>
                                    </div>
                                    <div style={styles.detailItem}><Phone size={12}/> {selectedControlAcc.phone || 'No Phone'}</div>
                                    <div style={styles.detailItem}><Hash size={12}/> ID: {selectedControlAcc.id}</div>
                                </div>
                            ) : <div style={styles.emptyMsg}><AlertCircle size={16}/> Select Control Account</div>}
                        </div>
                    )}
                </div>

                {/* --- MAIN ENTRY WORKSPACE TABLE --- */}
                <div style={styles.tableArea}>
                    <table style={styles.mainTable}>
                        <thead>
                            {header.voucher_type === 'JV' ? (
                                <tr style={styles.tableHead}>
                                    <th style={{width: '10%'}}>Ref</th>
                                    <th style={{width: '25%'}}>Debit Account (Dr)</th>
                                    <th style={{width: '25%'}}>Credit Account (Cr)</th>
                                    <th style={{width: '20%'}}>Narration / Remarks</th>
                                    <th style={{width: '15%', textAlign: 'right'}}>Amount (PKR)</th>
                                    <th style={{width: '5%'}}></th>
                                </tr>
                            ) : (
                                <tr style={styles.tableHead}>
                                    <th style={{width: '10%'}}>Ref</th>
                                    <th style={{width: '35%'}}>Account Title (Any Ledger)</th>
                                    <th style={{width: '30%'}}>Narration / Remarks</th>
                                    <th style={{width: '20%', textAlign: 'right'}}>Amount (PKR)</th>
                                    <th style={{width: '5%'}}></th>
                                </tr>
                            )}
                        </thead>
                        <tbody>
                            {entries.map((item, index) => (
                                <React.Fragment key={index}>
                                    {header.voucher_type === 'JV' ? (
                                        <tr>
                                            <td>
                                                <input 
                                                    style={styles.inputTable} 
                                                    value={item.invoice_no} 
                                                    placeholder="Ref" 
                                                    onChange={e => updateEntry(index, 'invoice_no', e.target.value)} 
                                                />
                                            </td>
                                            <td>
                                                <SearchableSelect 
                                                    isTable 
                                                    value={item.dr_account_id} 
                                                    placeholder="Search Debit Account..." 
                                                    onChange={acc => updateEntry(index, 'dr_account_id', acc.id, acc)} 
                                                />
                                            </td>
                                            <td>
                                                <SearchableSelect 
                                                    isTable 
                                                    value={item.cr_account_id} 
                                                    placeholder="Search Credit Account..." 
                                                    onChange={acc => updateEntry(index, 'cr_account_id', acc.id, acc)} 
                                                />
                                            </td>
                                            <td>
                                                <textarea 
                                                    rows={1}
                                                    style={styles.textareaTable} 
                                                    value={item.remarks} 
                                                    placeholder="Enter Remarks..." 
                                                    onChange={e => updateEntry(index, 'remarks', e.target.value)} 
                                                />
                                            </td>
                                            <td>
                                                <input 
                                                    type="number" 
                                                    style={styles.amountInput} 
                                                    value={item.amount} 
                                                    placeholder="0.00" 
                                                    onChange={e => updateEntry(index, 'amount', e.target.value)} 
                                                />
                                            </td>
                                            <td style={{textAlign:'center'}}>
                                                <button onClick={() => setEntries(entries.filter((_, i) => i !== index))} style={styles.delBtn}>
                                                    <Trash2 size={16}/>
                                                </button>
                                            </td>
                                        </tr>
                                    ) : (
                                        <tr>
                                            <td>
                                                <input 
                                                    style={styles.inputTable} 
                                                    value={item.invoice_no} 
                                                    placeholder="Ref" 
                                                    onChange={e => updateEntry(index, 'invoice_no', e.target.value)} 
                                                />
                                            </td>
                                            <td>
                                                <SearchableSelect 
                                                    isTable 
                                                    value={item.account_id} 
                                                    placeholder="Search Any Account..." 
                                                    onChange={acc => updateEntry(index, 'account_id', acc.id, acc)} 
                                                />
                                            </td>
                                            <td>
                                                <textarea 
                                                    rows={1}
                                                    style={styles.textareaTable} 
                                                    value={item.remarks} 
                                                    placeholder="Enter Remarks..." 
                                                    onChange={e => updateEntry(index, 'remarks', e.target.value)} 
                                                />
                                            </td>
                                            <td>
                                                <input 
                                                    type="number" 
                                                    style={styles.amountInput} 
                                                    value={item.amount} 
                                                    placeholder="0.00" 
                                                    onChange={e => updateEntry(index, 'amount', e.target.value)} 
                                                />
                                            </td>
                                            <td style={{textAlign:'center'}}>
                                                <button onClick={() => setEntries(entries.filter((_, i) => i !== index))} style={styles.delBtn}>
                                                    <Trash2 size={16}/>
                                                </button>
                                            </td>
                                        </tr>
                                    )}
                                    
                                    {header.voucher_type !== 'JV' && item.account_detail && (
                                        <tr>
                                            <td colSpan="5">
                                                <div style={styles.rowInfo}>
                                                    <ArrowRightLeft size={12} />
                                                    <b>{item.account_detail.title}</b> | ID: {item.account_detail.id} {item.account_detail.phone ? `| Ph: ${item.account_detail.phone}` : ''}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                    <button style={styles.addBtn} onClick={() => setEntries([...entries, header.voucher_type === 'JV' ? { dr_account_id: '', dr_account_detail: null, cr_account_id: '', cr_account_detail: null, invoice_no: '', remarks: '', amount: '' } : { account_id: '', account_detail: null, invoice_no: '', remarks: '', amount: '' }])}>
                        <Plus size={14} /> Add Row
                    </button>
                </div>

                {/* --- FOOTER TOTALS & BUTTONS --- */}
                <div style={styles.footerSection}>
                    <div>
                        <span style={styles.totalLabel}>Grand Total</span>
                        <h2 style={styles.grandTotal}>{totalAmount.toLocaleString()} <small style={{fontSize:'14px'}}>PKR</small></h2>
                    </div>
                    <div style={{display:'flex', gap:'12px', flexWrap:'wrap'}}>
                        <button style={styles.btnSecondary} onClick={exportPDF}>
                            <Download size={18} /> Preview PDF
                        </button>
                        <button style={styles.btnPostOnly} onClick={handlePostOnly} disabled={loading}>
                            <Send size={18} /> {loading ? 'Posting...' : 'Post Voucher'}
                        </button>
                        <button style={styles.btnPostPrint} onClick={handlePostAndPrint} disabled={loading}>
                            <Printer size={18} /> {loading ? 'Posting...' : 'Post & Print'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const styles = {
    container: { background: '#eef2f3', minHeight: '100vh', padding: '30px' },
    mainWrapper: { maxWidth: '1300px', margin: '0 auto', background: '#fff', borderRadius: '15px', boxShadow: '0 10px 30px rgba(0,0,0,0.08)', overflow: 'hidden' },
    topSection: { background: '#1c313a', padding: '16px 30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#fff' },
    brandGroup: { display: 'flex', alignItems: 'center', gap: '12px' },
    iconBox: { background: '#37474f', padding: '8px', borderRadius: '10px' },
    pageTitle: { margin: 0, fontSize: '18px', fontWeight: '800' },
    actionGroup: { display: 'flex', gap: '10px', alignItems: 'center' },
    dateTop: { background: '#263238', color: '#fff', border: '1px solid #455a64', padding: '8px 12px', borderRadius: '8px', outline: 'none', fontSize: '13px' },
    
    voucherNavBar: { display: 'flex', background: '#eceff1', padding: '12px 30px', gap: '12px', borderBottom: '1px solid #cfd8dc', flexWrap: 'wrap' },
    navTab: { flex: 1, minWidth: '170px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '12px 15px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '10px', cursor: 'pointer', transition: 'all 0.2s ease' },
    navTabActive: { flex: 1, minWidth: '170px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '12px 15px', background: '#1c313a', color: '#fff', border: '1px solid #1c313a', borderRadius: '10px', cursor: 'pointer', boxShadow: '0 4px 10px rgba(0,0,0,0.12)' },
    navBadge: { background: '#00897b', color: '#fff', fontSize: '11px', fontWeight: 'bold', padding: '3px 8px', borderRadius: '6px' },
    navLabel: { fontSize: '12.5px', fontWeight: '700', whiteSpace: 'nowrap' },

    flowBar: { background: '#f1f5f9', color: '#334155', padding: '10px 30px', fontSize: '12px', fontWeight: '700', borderBottom: '1px solid #e2e8f0' },
    
    controlBox: { padding: '20px 30px', display: 'grid', gridTemplateColumns: '1.5fr 1.5fr 1fr', gap: '20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', alignItems: 'start' },
    sectionLabel: { display: 'block', fontSize: '11px', fontWeight: '800', color: '#546e7a', textTransform: 'uppercase', marginBottom: '6px' },
    inputMain: { width: '100%', padding: '12px', border: '1px solid #cbd5e1', borderRadius: '10px', outline: 'none', background: '#fff', boxSizing: 'border-box' },
    
    detailCardActive: { background: '#fff', border: '1px solid #c8e6c9', borderRadius: '12px', padding: '12px 15px', height: '100%', boxSizing: 'border-box' },
    detailCardEmpty: { border: '2px dashed #cbd5e1', borderRadius: '12px', padding: '15px', textAlign: 'center', color: '#94a3b8', height: '100%', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    detailGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' },
    detailItem: { fontSize: '11px', color: '#546e7a', display: 'flex', alignItems: 'center', gap: '4px' },
    emptyMsg: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '12px' },
    
    tableArea: { padding: '20px 30px', overflowX: 'auto' },
    mainTable: { width: '100%', borderCollapse: 'separate', borderSpacing: '0 10px' },
    tableHead: { textAlign: 'left', fontSize: '11px', color: '#64748b', textTransform: 'uppercase' },
    
    inputTable: { width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', outline: 'none', background: '#fff', boxSizing: 'border-box' },
    textareaTable: { width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', outline: 'none', resize: 'vertical', minHeight: '40px', fontFamily: 'inherit', background: '#fff', boxSizing: 'border-box' },
    amountInput: { width: '100%', padding: '10px', border: '2px solid #cbd5e1', borderRadius: '8px', textAlign: 'right', fontWeight: '800', background: '#fffbe6', fontSize: '15px', boxSizing: 'border-box' },
    
    rowInfo: { background: '#f0f9ff', padding: '6px 12px', borderRadius: '6px', fontSize: '11px', color: '#0369a1', display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px', marginBottom: '8px' },
    addBtn: { marginTop: '10px', padding: '9px 18px', background: '#fff', border: '1px solid #1c313a', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' },
    
    footerSection: { padding: '20px 30px', background: '#fcfcfc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' },
    totalLabel: { fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' },
    grandTotal: { margin: 0, fontSize: '28px', fontWeight: '900', color: '#1c313a' },
    btnSecondary: { background: '#fff', border: '1px solid #cbd5e1', color: '#334155', padding: '12px 20px', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold', fontSize: '13px' },
    btnPostOnly: { background: '#2563eb', color: '#fff', border: 'none', padding: '12px 22px', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold', fontSize: '13px' },
    btnPostPrint: { background: '#16a34a', color: '#fff', border: 'none', padding: '12px 22px', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold', fontSize: '13px' },
    
    delBtn: { border: 'none', background: 'none', color: '#94a3b8', cursor: 'pointer', padding: '6px', borderRadius: '6px' },
    dropdownItem: { padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }
};

const loaderStyles = {
    overlay: {
        position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
        backgroundColor: 'rgba(28, 49, 58, 0.85)', backdropFilter: 'blur(4px)',
        zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column'
    },
    container: {
        backgroundColor: '#fff', padding: '40px 60px', borderRadius: '16px',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', display: 'flex',
        flexDirection: 'column', alignItems: 'center', gap: '16px'
    },
    spinner: {
        width: '50px', height: '50px', border: '4px solid #e2e8f0',
        borderTop: '4px solid #1c313a', borderRadius: '50%', animation: 'spin 1s linear infinite'
    },
    text: { margin: 0, color: '#1c313a', fontSize: '18px', fontWeight: '700', fontFamily: '"Inter", sans-serif' },
    subText: { margin: 0, color: '#64748b', fontSize: '13px', fontFamily: '"Inter", sans-serif' }
};

export default VoucherSystem;